import type { RingFinish } from '@jouzen/ecom-components';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import { OUT_OF_STOCK } from '@/app/[locale]/product/_consts';
import {
  type PDPContextType,
  usePDPContext,
} from '@/app/[locale]/product/_contexts/PDPProvider';
import type { PageParams } from '@/app/[locale]/product/rings/oura-ring-4/(wizard)/_types/pageParams';
import { POP_PATHNAME } from '@/app/consts/pathnames';
import { RingStyleEnum } from '@/app/enums/RingStyleEnum';

export interface Finish {
  isValid: boolean;
  value: RingFinish | '';
}

export interface CurrentRouteValues {
  finish: Finish;
  isReady: boolean;
  pushFinish: (value: RingFinish) => void;
}

const EXCLUDED_SEGMENTS = ['', 'product', 'rings'];

const useCurrentRoute = ({
  locale,
}: Partial<PageParams>): CurrentRouteValues => {
  const { getFinishStockStatus }: PDPContextType = usePDPContext();

  const pathname = usePathname();
  const segments = pathname
    .split('/')
    .filter((segment) => ![...EXCLUDED_SEGMENTS, locale].includes(segment));
  const routes = useMemo(() => ({ ...segments }), [segments]);

  // check finish
  const finish = useMemo(() => {
    const finishValue = routes[1] ?? '';
    const value = finishValue as RingFinish;
    const isValid =
      getFinishStockStatus({
        style: RingStyleEnum.OuraRing4,
        finish: finishValue as RingFinish,
        isOuraRing4: true,
      }) !== OUT_OF_STOCK;

    return {
      value,
      isValid,
    };
  }, [getFinishStockStatus, routes]);

  // Helper function for consistent options
  const pushRing = useCallback((subpath: string) => {
    window.history.pushState(null, '', `${POP_PATHNAME}/${subpath}`);
  }, []);

  const pushFinish = useCallback(
    (value: RingFinish) => {
      if (value !== finish.value) {
        pushRing(`${RingStyleEnum.OuraRing4}/${value}`);
      }
    },
    [finish.value, pushRing],
  );

  // used to hide any page flicker while the route gets sorted out
  const isReady = finish.isValid;

  return {
    isReady,
    finish,
    pushFinish,
  };
};

export default useCurrentRoute;
